import React, { useEffect, useState, useCallback } from "react";
import "../dashboard/dashboard.css";
import { Grid, Card, CardContent, Typography, Box, Select, MenuItem, FormControl, InputLabel, Button, CircularProgress, TextField, Chip, IconButton, Tooltip, Divider, Paper } from "@mui/material";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import * as DashboardActions from '../../redux/actions/dashboardAction.js'
import { DoubleRightArrowSvg } from "../../assets/svg/index.js";
import { useNavigate } from "react-router-dom";
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TempleBuddhistIcon from '@mui/icons-material/TempleBuddhist';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bharatdarshan.app/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dashboardData } = useSelector(state => state.dashboard);
  
  // State for comprehensive dashboard
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [filter, setFilter] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch comprehensive stats from new API
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/dashboard/stats?filter=${filter}`;
      
      if (filter === 'custom' && customStartDate && customEndDate) {
        url = `${API_BASE_URL}/dashboard/stats?startDate=${customStartDate}&endDate=${customEndDate}`;
      }
      
      const [statsResponse, dailyResponse] = await Promise.all([
        fetch(url),
        fetch(`${API_BASE_URL}/dashboard/daily-stats?days=30`)
      ]);
      
      const statsData = await statsResponse.json();
      const dailyData = await dailyResponse.json();
      
      if (statsData.success) {
        setStats(statsData.data);
        setLastUpdated(new Date());
      }
      
      if (dailyData.success) {
        setDailyStats(dailyData.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, customStartDate, customEndDate]);

  useEffect(() => {
    dispatch(DashboardActions.getDashboard());
    fetchDashboardStats();
  }, [dispatch, fetchDashboardStats]);

  // Auto refresh every minute
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [autoRefresh, fetchDashboardStats]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)} K`;
    }
    return `₹${amount?.toFixed(0) || 0}`;
  };

  // Format number
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num?.toString() || '0';
  };

  // Revenue breakdown chart
  const revenueBreakdownChart = {
    options: {
      chart: { type: 'donut', fontFamily: 'inherit' },
      labels: ['Recharge', 'Flights', 'Hotels', 'Yatra', 'AI Calls', 'Astrology', 'E-commerce', 'Wallet'],
      colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#673AB7', '#FF5722'],
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + '%' },
      plotOptions: { pie: { donut: { size: '55%', labels: { show: true, total: { show: true, label: 'Total Revenue', formatter: () => formatCurrency(stats?.totalRevenue || 0) } } } } },
      responsive: [{ breakpoint: 480, options: { chart: { width: 280 }, legend: { position: 'bottom' } } }]
    },
    series: stats ? [
      stats.services?.recharge?.revenue || 0,
      stats.services?.flights?.revenue || 0,
      stats.services?.hotels?.revenue || 0,
      stats.services?.yatra?.revenue || 0,
      stats.services?.aiCalls?.revenue || 0,
      (stats.services?.astrology?.chatRevenue || 0) + (stats.services?.astrology?.callRevenue || 0),
      stats.services?.ecommerce?.revenue || 0,
      stats.services?.wallet?.amount || 0
    ] : [0, 0, 0, 0, 0, 0, 0, 0]
  };

  // User growth chart
  const userGrowthChart = {
    options: {
      chart: { type: 'area', fontFamily: 'inherit', toolbar: { show: false } },
      xaxis: { categories: dailyStats?.userGrowth?.map(d => d._id) || [], labels: { rotate: -45, style: { fontSize: '10px' } } },
      colors: ['#4CAF50'],
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      title: { text: 'User Growth (Last 30 Days)', align: 'left', style: { fontSize: '14px', fontWeight: 600 } }
    },
    series: [{ name: 'New Users', data: dailyStats?.userGrowth?.map(d => d.count) || [] }]
  };

  // Recharge trend chart
  const rechargeTrendChart = {
    options: {
      chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
      xaxis: { categories: dailyStats?.rechargeGrowth?.map(d => d._id) || [], labels: { rotate: -45, style: { fontSize: '10px' } } },
      colors: ['#2196F3'],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      title: { text: 'Recharge Trend (Last 30 Days)', align: 'left', style: { fontSize: '14px', fontWeight: 600 } }
    },
    series: [{ name: 'Recharges', data: dailyStats?.rechargeGrowth?.map(d => d.count) || [] }]
  };

  // Service comparison chart
  const serviceComparisonChart = {
    options: {
      chart: { type: 'bar', fontFamily: 'inherit', stacked: false, toolbar: { show: false } },
      xaxis: { categories: ['Recharge', 'AI Calls', 'Astrology', 'Milan Sathi', 'E-commerce', 'Puja'] },
      colors: ['#4CAF50', '#2196F3'],
      plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 4 } },
      legend: { position: 'top' },
      title: { text: 'Service Transactions', align: 'left', style: { fontSize: '14px', fontWeight: 600 } }
    },
    series: [
      {
        name: 'Count',
        data: stats ? [
          stats.services?.recharge?.total || 0,
          stats.services?.aiCalls?.total || 0,
          (stats.services?.astrology?.chats || 0) + (stats.services?.astrology?.calls || 0),
          stats.services?.milanSathi?.transactions || 0,
          stats.services?.ecommerce?.orders || 0,
          stats.services?.puja?.total || 0
        ] : [0, 0, 0, 0, 0, 0]
      }
    ]
  };

  // Stat Card Component
  const StatCard = ({ title, value, subValue, icon, color, onClick, trend }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 4 } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold" color={color}>
              {value}
            </Typography>
            {subValue && (
              <Typography variant="body2" color="textSecondary">
                {subValue}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={0.5}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ backgroundColor: `${color}20`, borderRadius: 2, p: 1.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Balance Card Component
  const BalanceCard = ({ title, value, icon, color, status }) => (
    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`, border: `1px solid ${color}30` }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" component="div" fontWeight="bold">
              {value}
            </Typography>
            {status && (
              <Chip label={status} size="small" color={status === 'Active' ? 'success' : 'warning'} sx={{ mt: 1 }} />
            )}
          </Box>
          <Box sx={{ backgroundColor: `${color}20`, borderRadius: '50%', p: 1.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Dashboard</Typography>
          <Typography variant="body2" color="textSecondary">
            Complete overview of all Bharat Darshan services
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Filter Selection */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select value={filter} label="Period" onChange={(e) => setFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          
          {filter === 'custom' && (
            <>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" size="small" onClick={fetchDashboardStats}>
                Apply
              </Button>
            </>
          )}
          
          <Tooltip title={`Last updated: ${lastUpdated?.toLocaleTimeString() || 'Never'}`}>
            <IconButton onClick={fetchDashboardStats} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Chip
            label={autoRefresh ? 'Auto Refresh: ON' : 'Auto Refresh: OFF'}
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      {/* API Balances */}
      <Typography variant="h6" fontWeight="bold" mb={2}>💰 API Balances</Typography>
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <BalanceCard
            title="TBO Balance (Flight/Hotel)"
            value={formatCurrency(stats?.balances?.tbo || 0)}
            icon={<FlightIcon sx={{ color: '#2196F3', fontSize: 32 }} />}
            color="#2196F3"
            status={stats?.balances?.tbo > 10000 ? 'Active' : 'Low Balance'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <BalanceCard
            title="Pay2New Balance (Recharge)"
            value={formatCurrency(stats?.balances?.pay2new || 0)}
            icon={<PhoneAndroidIcon sx={{ color: '#4CAF50', fontSize: 32 }} />}
            color="#4CAF50"
            status={stats?.balances?.pay2new > 5000 ? 'Active' : 'Low Balance'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={formatNumber(stats?.users?.total || 0)}
            subValue={`${formatNumber(stats?.users?.new || 0)} new in period`}
            icon={<PeopleIcon sx={{ color: '#9C27B0', fontSize: 32 }} />}
            color="#9C27B0"
            onClick={() => navigate('/user')}
            trend={stats?.users?.new > 0 ? `+${stats.users.new} new` : null}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats?.totalRevenue || 0)}
            subValue="From all services"
            icon={<LocalAtmIcon sx={{ color: '#FF5722', fontSize: 32 }} />}
            color="#FF5722"
          />
        </Grid>
      </Grid>

      {/* Services Stats */}
      <Typography variant="h6" fontWeight="bold" mb={2}>📊 Services Overview</Typography>
      <Grid container spacing={2} mb={4}>
        {/* Recharge */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recharge"
            value={formatNumber(stats?.services?.recharge?.total || 0)}
            subValue={`✓ ${stats?.services?.recharge?.success || 0} | ✗ ${stats?.services?.recharge?.failed || 0}`}
            icon={<PhoneAndroidIcon sx={{ color: '#4CAF50', fontSize: 28 }} />}
            color="#4CAF50"
            onClick={() => navigate('/service-history/recharge')}
          />
        </Grid>

        {/* Flights */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Flight Bookings"
            value={formatNumber(stats?.services?.flights?.total || 0)}
            subValue={formatCurrency(stats?.services?.flights?.revenue || 0)}
            icon={<FlightIcon sx={{ color: '#2196F3', fontSize: 28 }} />}
            color="#2196F3"
            onClick={() => navigate('/tbo-bookings/flights')}
          />
        </Grid>

        {/* Hotels */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hotel Bookings"
            value={formatNumber(stats?.services?.hotels?.total || 0)}
            subValue={formatCurrency(stats?.services?.hotels?.revenue || 0)}
            icon={<HotelIcon sx={{ color: '#FF9800', fontSize: 28 }} />}
            color="#FF9800"
            onClick={() => navigate('/tbo-bookings/hotels')}
          />
        </Grid>

        {/* Yatra */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Yatra Bookings"
            value={formatNumber(stats?.services?.yatra?.total || 0)}
            subValue={formatCurrency(stats?.services?.yatra?.revenue || 0)}
            icon={<LocationOnIcon sx={{ color: '#9C27B0', fontSize: 28 }} />}
            color="#9C27B0"
            onClick={() => navigate('/yatra')}
          />
        </Grid>

        {/* AI Calls */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="AI Astrologer Calls"
            value={formatNumber(stats?.services?.aiCalls?.total || 0)}
            subValue={`${Math.round((stats?.services?.aiCalls?.duration || 0) / 60)} mins | ${formatCurrency(stats?.services?.aiCalls?.revenue || 0)}`}
            icon={<SmartToyIcon sx={{ color: '#00BCD4', fontSize: 28 }} />}
            color="#00BCD4"
          />
        </Grid>

        {/* Milan Sathi */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Milan Sathi"
            value={formatNumber(stats?.services?.milanSathi?.users || 0)}
            subValue={`${stats?.services?.milanSathi?.transactions || 0} transactions | ${formatCurrency(stats?.services?.milanSathi?.revenue || 0)}`}
            icon={<FavoriteIcon sx={{ color: '#E91E63', fontSize: 28 }} />}
            color="#E91E63"
            onClick={() => navigate('/milan-sathi')}
          />
        </Grid>

        {/* Astrology */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Astrology Consultations"
            value={`${stats?.services?.astrology?.chats || 0} + ${stats?.services?.astrology?.calls || 0}`}
            subValue={`Chats + Calls | ${formatCurrency((stats?.services?.astrology?.chatRevenue || 0) + (stats?.services?.astrology?.callRevenue || 0))}`}
            icon={<SupportAgentIcon sx={{ color: '#673AB7', fontSize: 28 }} />}
            color="#673AB7"
            onClick={() => navigate('/history')}
          />
        </Grid>

        {/* E-commerce */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="E-commerce Orders"
            value={formatNumber(stats?.services?.ecommerce?.orders || 0)}
            subValue={`${stats?.services?.ecommerce?.products || 0} products | ${formatCurrency(stats?.services?.ecommerce?.revenue || 0)}`}
            icon={<ShoppingCartIcon sx={{ color: '#795548', fontSize: 28 }} />}
            color="#795548"
            onClick={() => navigate('/astro-mall')}
          />
        </Grid>

        {/* Puja */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Puja Services"
            value={formatNumber(stats?.services?.puja?.total || 0)}
            subValue="Total Puja offerings"
            icon={<TempleBuddhistIcon sx={{ color: '#FF5722', fontSize: 28 }} />}
            color="#FF5722"
            onClick={() => navigate('/astro-puja')}
          />
        </Grid>

        {/* Wallet */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Wallet Recharges"
            value={formatNumber(stats?.services?.wallet?.recharges || 0)}
            subValue={formatCurrency(stats?.services?.wallet?.amount || 0)}
            icon={<AccountBalanceWalletIcon sx={{ color: '#607D8B', fontSize: 28 }} />}
            color="#607D8B"
            onClick={() => navigate('/transaction')}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Typography variant="h6" fontWeight="bold" mb={2}>📈 Analytics</Typography>
      <Grid container spacing={3}>
        {/* Revenue Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Revenue Breakdown</Typography>
            <Chart
              options={revenueBreakdownChart.options}
              series={revenueBreakdownChart.series}
              type="donut"
              height={350}
            />
          </Paper>
        </Grid>

        {/* User Growth */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Chart
              options={userGrowthChart.options}
              series={userGrowthChart.series}
              type="area"
              height={350}
            />
          </Paper>
        </Grid>

        {/* Recharge Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Chart
              options={rechargeTrendChart.options}
              series={rechargeTrendChart.series}
              type="bar"
              height={350}
            />
          </Paper>
        </Grid>

        {/* Service Comparison */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Chart
              options={serviceComparisonChart.options}
              series={serviceComparisonChart.series}
              type="bar"
              height={350}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Footer Info */}
      <Box mt={4} pt={2} borderTop="1px solid #e0e0e0">
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Dashboard auto-refreshes every 1 minute • Last updated: {lastUpdated?.toLocaleString() || 'Never'}
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;
